import { useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetCustomerCallsAnalyticsQuery, useLazyGetMonthlyEngagementSummaryQuery } from "store/api/engagementApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, userLogoSelector } from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { isValidDateRange } from "utils/dateUtils"
import { triggerFileDownload } from "utils/triggerFileDownload"
import { buildMonthlyEngagementReportData } from "pages/contractor/MonthlyEngagementPage/reportData"

type MonthlyEngagementDownloadParams = {
  contractor_id: number
  reporting_period_from: string
  reporting_period_to: string
}

export const useMonthlyEngagementPdfDownloader = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const contractor = userProfile?.contractor
  const currentTheme = useAppSelector(contractorThemeSelector)
  const userLogo = useAppSelector(userLogoSelector)
  const isPartnerAnalyticsEnabled = useAppSelector(
    isFeatureEnabled(FeatureFlag.MonthlyEngagementReportPartnersAnalytics),
  )
  const isCustomerCallsEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.MonthlyEngagementReportCustomerCalls))
  const [getMonthlyEngagementSummary] = useLazyGetMonthlyEngagementSummaryQuery()
  const [getCustomerCallsAnalytics] = useLazyGetCustomerCallsAnalyticsQuery()
  const [isDownloading, setIsDownloading] = useState(false)
  const canUseCustomTheme =
    contractor?.subscription_plan === SubscriptionPlan.E_PLUS || Boolean(userProfile?.enterprise)
  const backgroundGradient = canUseCustomTheme
    ? currentTheme.backgroundGradient
    : ContractorThemes.default.backgroundGradient

  const generatePdf = async (params: MonthlyEngagementDownloadParams) => {
    if (isDownloading) return

    if (!isValidDateRange(params.reporting_period_from, params.reporting_period_to)) {
      toast.error("Invalid reporting period")
      return
    }

    setIsDownloading(true)
    try {
      const summaryResult = await getMonthlyEngagementSummary(params).unwrap()
      const customerCalls = isCustomerCallsEnabled
        ? await getCustomerCallsAnalytics(params)
            .unwrap()
            .catch(err => {
              console.error("Monthly engagement customer calls PDF data unavailable:", err)
              return undefined
            })
        : undefined
      const report = buildMonthlyEngagementReportData({ summary: summaryResult, customerCalls })
      const { generateMonthlyEngagementPdf } =
        await import("pages/contractor/MonthlyEngagementPage/pdf/generateMonthlyEngagementPdf")
      return await generateMonthlyEngagementPdf(
        report,
        backgroundGradient,
        userLogo,
        isPartnerAnalyticsEnabled,
        isCustomerCallsEnabled,
      )
    } catch (err) {
      console.error("Monthly engagement PDF generation failed:", err)
      toast.error("PDF download failed. Please try again later.")
    } finally {
      setIsDownloading(false)
    }
  }

  const downloadMonthlyEngagementPdf = async (params: MonthlyEngagementDownloadParams) => {
    const blob = await generatePdf(params)

    if (blob) {
      triggerFileDownload(blob, "monthly-engagement-report.pdf")
    }
  }

  const viewMonthlyEngagementPdf = async (params: MonthlyEngagementDownloadParams) => {
    if (isDownloading) return

    const viewerWindow = window.open("", "_blank")

    if (!viewerWindow) {
      toast.error("Unable to open the PDF viewer. Please allow pop-ups and try again.")
      return
    }

    viewerWindow.document.title = "Monthly Engagement Report"
    viewerWindow.document.body.textContent = "Generating PDF..."

    const blob = await generatePdf(params)

    if (!blob) {
      viewerWindow.close()
      return
    }

    const url = URL.createObjectURL(blob)
    viewerWindow.location.replace(url)
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  return {
    downloadMonthlyEngagementPdf,
    viewMonthlyEngagementPdf,
    isDownloadingMonthlyEngagementPdf: isDownloading,
  }
}
